<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { i18n } from "@/plugin/vue-i18n";
import { PICTURE_CHOICE_ELEMENT_TYPE } from "@/types/pictureChoice";
import { PRODUCT_TYPE } from "@/util/constants";
import { isCfV1Enabled } from "@/util/customFieldsPhase1";
import { legacyDatePickerSidebarField, standardFields } from "@/util/methods";
import { QUICK_ADD_FIRST } from "@/validation/types";
import { UIDraggableElement, UITextXsMedium } from "@gohighlevel/ghl-ui";
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";

const store = useAppStore();

if (store.product === PRODUCT_TYPE.FORM) {
  delete standardFields.quizQuestions;
}
if (store.product === PRODUCT_TYPE.SURVEY) {
  delete standardFields.submit;
  delete standardFields.quizQuestions;
}
if (store.product === PRODUCT_TYPE.QUIZ) {
  delete standardFields.submit;
  delete standardFields.integration;
  const pictureChoice = standardFields.choiceElements?.group.find(
    (group) => group.builderElementType === PICTURE_CHOICE_ELEMENT_TYPE
  );
  const hasPictureChoiceInQuizQuestions = standardFields.quizQuestions.group.some(
    (group) => group.builderElementType === PICTURE_CHOICE_ELEMENT_TYPE
  );
  if (pictureChoice && !hasPictureChoiceInQuizQuestions) {
    standardFields.quizQuestions.group.push(pictureChoice);
  }
  delete standardFields.choiceElements;
  standardFields.other.group.splice(3, 1);
  standardFields.text.group.splice(0, 2);
}
if (!store.isPaymentEnabled) {
  //Removing paymnet from form
  delete standardFields.integration;
}

// Sub-accounts outside the rollout keep the pre-rollout "Date Picker" sidebar
// entry (legacy label/placeholder, no "New" badge). Read at setup, like the other
// host-resolved flags above, because the Postmate model lands before mount.
if (!isCfV1Enabled(store.locationId)) {
  const dateFieldIndex = standardFields.other.group.findIndex(
    (group) => group.tag === "date" && group.dataType === "DATE"
  );
  if (dateFieldIndex > -1) {
    standardFields.other.group[dateFieldIndex] = legacyDatePickerSidebarField();
  }
}

if (![PRODUCT_TYPE.FORM, PRODUCT_TYPE.SURVEY].includes(store.product)) {
  const index = standardFields.address.group.findIndex((group) => group.tag === "group_address");
  if (index > -1) {
    standardFields.address.group.splice(index, 1);
  }
  const addressIndex = standardFields.address.group.findIndex((group) => group.tag === "address");
  if (addressIndex > -1) {
    standardFields.address.group[addressIndex].hideInLeftSideBar = false;
  }
}

const quickAdd = ref(standardFields);

/**
 * Keeps rollout-gated Quick Add fields hidden for sub-accounts outside the shared
 * Custom Fields Phase 1 rollout without affecting already-persisted elements.
 */
const isLocationGatedFieldVisible = (group: any): boolean => {
  if (isCfV1Enabled(store.locationId)) return true;

  return (
    group?.dataType !== "URL" &&
    group?.builderElementType !== PICTURE_CHOICE_ELEMENT_TYPE
  );
};

/**
 * A recommendation can ask for an element the asset does not have yet. There is
 * nothing on the canvas to select, so the palette entry is marked instead and
 * scrolled into view. The mark clears itself — it points the user at the tile
 * once, it is not a persistent state.
 */
const HIGHLIGHT_MS = 4000;
const highlightTimer = ref<ReturnType<typeof setTimeout> | undefined>();

/** The tile a "start here" recommendation points at when it names no element. */
const firstVisibleTag = computed<string | undefined>(() => {
  for (const category of Object.values(quickAdd.value) as any[]) {
    for (const group of category?.group ?? []) {
      if (group?.hideInLeftSideBar) continue;
      if (!isLocationGatedFieldVisible(group)) continue;
      return group?.tag;
    }
  }
  return undefined;
});

const isHighlighted = (group: any): boolean => {
  const wanted = store.quickAddHighlightTag;
  if (!wanted) return false;
  if (wanted === QUICK_ADD_FIRST) return group?.tag === firstVisibleTag.value;
  return group?.tag === wanted;
};

watch(
  () => store.quickAddHighlightTag,
  async (tag) => {
    clearTimeout(highlightTimer.value);
    if (!tag) return;
    await nextTick();
    // scrollIntoView would scroll every scrollable ancestor, which drags the
    // builder header and the canvas out of view with no way back. Only this
    // list should move, so its scrollTop is set directly.
    const list = document.querySelector("#standard-container");
    const tile = list?.querySelector(".quick-add-highlight");
    if (list && tile) {
      const offset =
        tile.getBoundingClientRect().top - list.getBoundingClientRect().top;
      list.scrollTo({
        top:
          list.scrollTop + offset - list.clientHeight / 2 + tile.clientHeight / 2,
        behavior: "smooth",
      });
    }
    highlightTimer.value = setTimeout(() => {
      store.quickAddHighlightTag = undefined;
    }, HIGHLIGHT_MS);
  }
);

onBeforeUnmount(() => clearTimeout(highlightTimer.value));

const getDisplayLabel = (group: any): string => {
  if (group?.tag === "header") {
    return i18n.global.t("fields.customized.text");
  }
  if (group?.tag === "address") {
    return i18n.global.t("fields.address.address");
  }
  const raw = typeof group?.label === "string" ? group.label : "";
  return raw.replace(/<[^>]*>/g, "");
};
</script>
<template>
  <div id="standard-container" class="standard-list-container">
    <div v-for="(category, index) in quickAdd" :key="index" class="flex flex-col gap-[12px]">
      <UITextXsMedium class="mt-2 px-2">{{ category.label }}</UITextXsMedium>
      <VueDraggableNext
        :list="category.group"
        :sort="false"
        class="standard-draggable-container gap-1"
        ghost-class="sortable-ghost"
        :group="{ name: 'elements', pull: 'clone', put: false }"
      >
        <template v-for="(group, i) in category.group" :key="i">
          <div
            v-if="isLocationGatedFieldVisible(group)"
            :class="[
              group.hideInLeftSideBar ? 'hidden' : 'p-1',
              isHighlighted(group) ? 'quick-add-highlight' : '',
            ]"
          >
            <UIDraggableElement
              :id="getDisplayLabel(group) + i"
              :icon="(group.icon as any)"
              :label="getDisplayLabel(group)"
            />
          </div>
        </template>
      </VueDraggableNext>
    </div>
  </div>
</template>

<style>
.standard-list-container {
  height: 84vh;
  padding-top: 5px;
  padding-bottom: v-bind("store.isNewBuilderLayoutEnabled ? '100px' : '50px'");
  padding-left: 8px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.element-cards .gui__builder-card {
  display: table;
  height: 100%;
}

#standard-container .gui__builder-card--label {
  padding-bottom: 5px;
  text-overflow: clip;
}

#standard-container .gui__builder-card {
  width: 100px;
  height: 100px;
  padding: 12px 0px;
  position: relative;
}
.quick-add-highlight .gui__builder-card {
  outline: 2px solid #155eef;
  outline-offset: -2px;
  border-radius: 6px;
  animation: quick-add-pulse 1.2s ease-in-out 2;
}

@keyframes quick-add-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(21, 94, 239, 0);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(21, 94, 239, 0.25);
  }
}

.standard-item {
  background-color: white;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  cursor: all-scroll;
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none;
}
.standard-draggable-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: start;
}
.standard-item {
  box-shadow: 1px 1px 0px 0px #dee2e6;
}
.icon-container {
  width: 15px;
  height: 15px;
  margin: auto 0;
  padding: 0;
  align-items: center;
  display: flex;
}

.gui__builder-card--icon img,
.gui__builder-card--icon svg {
  height: 60px !important;
  width: 60px !important;
}
.gui__builder-card--icon svg {
  height: 35px;
  width: 35px;
}
</style>
